import { SortDescriptor } from '@progress/kendo-data-query';
import { Render } from 'Components/Display';
import {
  FilterBarProvider,
  FilterBarProviderProps,
  useDebouncedFilterValues,
  useFilterBarState,
  useGlobalContext,
  useGridState,
} from 'Context';
import download from 'downloadjs';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useApiWorker } from 'Utilities';
import { useHistory, useLocation } from 'react-router-dom';
import { AsyncGrid } from '../AsyncGrid';
import { NonInteractiveAutoRefresh } from '../AutoRefresh/NonInteractiveAutoRefresh';
import { ColumnSelector } from '../ColumnSelector/ColumnSelector';
import {
  SelectableAsyncGrid,
  SelectableAsyncGridProps,
} from '../SelectableGrid';
import TableHeader from '../TableHeader';
import { FilterBar, FilterBarProps } from './FilterBar';
import { AsyncGridProps } from '../types';
import { grid } from '@mui/system';
import { FilterBarFilterType } from 'Models';

export type FilterBarGridProps = AsyncGridProps & {
  noInitialLoad?: boolean;
  filterBarProps: FilterBarProps & FilterBarProviderProps;
  isColumnSelectorEnabled?: boolean;
  headerTitle?: string;
  forceExportAction?: (params: any) => void;
  headerDisabled?: boolean;
  defaultFiltersEnabled?: boolean;
  fileUploaderEndpoint?: string;
  fileUploaderFormEndpoint?: string;
  fileUploaderDisabledLabel?: string;
  uploadExtraProps?: { [key: string]: any };
  forceEmpty?: boolean;
  customHeaderSection?: React.ReactNode;
  hideFilterIcon?: boolean;
  hideExportIcon?: boolean;
  onFiltersEnabledChange?: (enabled: boolean) => void;
  disableUrlSync?: boolean;
};

export type FilteredGridProps = AsyncGridProps & {
  noInitialLoad?: boolean;
  headerTitle?: string;
  hasColumnSelector?: boolean;
  forceExportAction?: (params: any) => void;
  headerDisabled?: boolean;
  defaultFiltersEnabled?: boolean;
  fileUploaderEndpoint?: string;
  fileUploaderFormEndpoint?: string;
  fileUploaderDisabledLabel?: string;
  uploadExtraProps?: { [key: string]: any };
  forceEmpty?: boolean;
  customHeaderSection?: React.ReactNode;
  hideFilterIcon?: boolean;
  hideExportIcon?: boolean;
  onFiltersEnabledChange?: (enabled: boolean) => void;
  disableUrlSync?: boolean;
};

export const FilteredGrid: React.FC<FilteredGridProps> = ({
  children,
  noInitialLoad,
  forceExportAction,
  fileUploaderEndpoint,
  fileUploaderDisabledLabel,
  defaultFiltersEnabled = false,
  fileUploaderFormEndpoint,
  uploadExtraProps,
  customHeaderSection,
  hideFilterIcon,
  hideExportIcon,
  onFiltersEnabledChange,
  disableUrlSync = false,
  ...gridProps
}) => {
  const history = useHistory();
  const location = useLocation();
  const [searchKey, setSearchKey] = React.useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('searchKey') || '';
  });
  const { filterValues, filterConfigurations } = useFilterBarState();
  const [filters, setFilters] = React.useState<any>({});
  const [sorting, setSorting] = React.useState<SortDescriptor[]>([]);
  const [filtersEnabled, setFiltersEnabled] = React.useState(
    defaultFiltersEnabled
  );
  const [isUpdatingFromUrl, setIsUpdatingFromUrl] = React.useState(false);
  const prevFiltersRef = React.useRef<any>({});

  // Convert filters object to Kendo Grid CompositeFilterDescriptor format
  const getGridFilterFromFilters = (filtersObj: any) => {
    const filterDescriptors = Object.entries(filtersObj).map(
      ([field, value]) => ({
        field,
        operator: 'contains',
        value,
      })
    );

    return {
      logic: 'and' as const,
      filters: filterDescriptors,
    };
  };

  // Read URL params and update state when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Read all other params as potential column filters
    const urlFilters: any = {};
    searchParams.forEach((value, key) => {
      if (key !== 'searchKey') {
        urlFilters[key] = value;
      }
    });

    // Static filters are never synced to/from URL — preserve their current values
    filterConfigurations.forEach((config) => {
      if (
        config.type === FilterBarFilterType.Static ||
        config.type === FilterBarFilterType.StaticWithDisplay
      ) {
        const val = filterValues[config.name];
        if (val !== undefined && val !== null && val !== '') {
          urlFilters[config.name] = val;
        }
      }
    });

    setIsUpdatingFromUrl(true);
    setFilters(urlFilters);
    // Reset flag after state update
    setTimeout(() => setIsUpdatingFromUrl(false), 0);
  }, [location.search]);

  // change the searchKey when url changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('searchKey') || '';
    setIsUpdatingFromUrl(true);
    setSearchKey(searchQuery);
    setTimeout(() => setIsUpdatingFromUrl(false), 0);
  }, [location.search]);

  // Sync searchKey to URL (only when not updating from URL)
  useEffect(() => {
    if (isUpdatingFromUrl || disableUrlSync) return;

    const params = new URLSearchParams(location.search);

    if (searchKey && searchKey.trim() !== '') {
      params.set('searchKey', searchKey);
    } else {
      params.delete('searchKey');
    }

    const newSearch = params.toString();
    const currentSearch = location.search.substring(1);

    if (newSearch !== currentSearch) {
      history.replace({ search: newSearch });
    }
  }, [searchKey]);

  // Sync filters to URL (only when not updating from URL)
  useEffect(() => {
    if (isUpdatingFromUrl || disableUrlSync) return;

    const params = new URLSearchParams(location.search);

    // Get keys from previous filters to detect removals
    const prevKeys = Object.keys(prevFiltersRef.current);
    const currentKeys = Object.keys(filters);

    // Remove params that are no longer in filters
    prevKeys.forEach((key) => {
      if (!currentKeys.includes(key)) {
        params.delete(key);
      }
    });

    // Add/update current filters — skip static filters, they are never URL-synced
    Object.entries(filters).forEach(([key, value]) => {
      const config = filterConfigurations.find((c) => c.name === key);
      if (
        config?.type === FilterBarFilterType.Static ||
        config?.type === FilterBarFilterType.StaticWithDisplay
      ) {
        return;
      }
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, String(value));
      } else {
        params.delete(key);
      }
    });

    // Update ref for next comparison
    prevFiltersRef.current = { ...filters };

    const newSearch = params.toString();
    const currentSearch = location.search.substring(1);

    if (newSearch !== currentSearch) {
      history.replace({ search: newSearch });
    }
  }, [filters, history, isUpdatingFromUrl, searchKey]);

  useEffect(() => {
    setIsUpdatingFromUrl(true);
    setFilters((prevFilters: any) => {
      const nextFilters: any = { ...prevFilters };
      // Sync configurations from FilterBarProvider
      // We overwrite existing values and REMOVE keys that are in configuration
      // but were cleared (missing from filterValues)
      filterConfigurations.forEach((config) => {
        if (config.type === FilterBarFilterType.DateRange) {
          const fromName = `${config.name}From`;
          const toName = `${config.name}To`;
          [fromName, toName].forEach((name) => {
            const val = filterValues[name];
            if (val === undefined || val === null || val === '') {
              delete nextFilters[name];
            } else {
              nextFilters[name] = val;
            }
          });
        } else {
          const val = filterValues[config.name];
          if (val === undefined || val === null || val === '') {
            delete nextFilters[config.name];
          } else {
            nextFilters[config.name] = val;
          }
        }
      });
      return nextFilters;
    });
  }, [filterValues, filterConfigurations]);

  const searchKeyChanged = (newSearchKey: string) => {
    setSearchKey(newSearchKey);
  };

  useEffect(() => {
    setFilters((prevFilters: any) => {
      const { sort, ...restFilters } = prevFilters;
      if (sorting.length > 0) {
        const sortItem = sorting[0];
        return {
          ...restFilters,
          sort: `${sortItem.field}-${sortItem.dir}`,
        };
      }
      return restFilters;
    });
  }, [sorting]);

  const API = useApiWorker();

  const exportCsv = () => {
    const exportEndpoint = gridProps.exportEndpoint;
    if (exportEndpoint === undefined && forceExportAction === undefined) return;

    const exportAction = async () => {
      try {
        const { data, headers } = await API.post(
          exportEndpoint || '/api/export/csv',
          {
            ...filters,
            searchKey,
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/csv',
              ...(gridProps.exportEndpointVersion && {
                'x-api-version': gridProps.exportEndpointVersion,
              }),
            },
          }
        );

        const mimeType = headers['content-type'];
        var dateStamp = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          day: '2-digit',
          month: '2-digit',
        })
          .format(new Date())
          .toString()
          .replace(/[\/\\]/g, '');

        var filename = `${'exported'}_${dateStamp}.${'csv'}`;
        download(data, filename, mimeType);
      } catch (err) {
        const error = err as any;
        toast.error(
          error ? error?.ErrorMessage : 'Error: Could not download file'
        );
      }
    };
    if (forceExportAction) {
      const params = { ...filterValues };
      forceExportAction(params);
      return;
    }
    exportAction();
  };
  const gridState = useGridState();
  const { areFiltersApplied = false } = useFilterBarState();
  return (
    <Render condition={!noInitialLoad || areFiltersApplied}>
      <NonInteractiveAutoRefresh
        onRefresh={() => {
          gridState.refreshGrid();
        }}
      />
      <TableHeader
        filtersEnabled={filtersEnabled}
        numberOfFiltersActive={Object.keys(filters).length}
        headerTitle={gridProps.headerTitle}
        onChangeValue={searchKeyChanged}
        onPressFilterIcon={() => {
          const next = !filtersEnabled;
          setFiltersEnabled(next);
          onFiltersEnabledChange?.(next);
        }}
        onPressDownloadIcon={exportCsv}
        headerDisabled={gridProps.headerDisabled}
        fileUploaderEndpoint={fileUploaderEndpoint}
        fileUploaderDisabledLabel={fileUploaderDisabledLabel}
        fileUploaderFormEndpoint={fileUploaderFormEndpoint}
        uploadExtraProps={uploadExtraProps}
        searchKey={searchKey}
        customHeaderSection={customHeaderSection}
        hideFilterIcon={hideFilterIcon}
        hideExportIcon={hideExportIcon}
      />
      <AsyncGrid
        search={{ ...filters, searchKey }}
        condition={!noInitialLoad || areFiltersApplied}
        hideNoRecordsAlert={noInitialLoad && !areFiltersApplied}
        refreshOnSearchChange
        onFiltersChange={setFilters}
        onSortingChange={setSorting}
        filtersEnabled={filtersEnabled}
        gridId={gridProps.gridId}
        filter={
          Object.keys(filters).length > 0
            ? getGridFilterFromFilters(filters)
            : undefined
        }
        {...gridProps}
      >
        {children}
      </AsyncGrid>
    </Render>
  );
};

export type FilterBarSelectableGridProps = SelectableAsyncGridProps & {
  filterBarProps: FilterBarProps & FilterBarProviderProps;
};

export const FilterBarSelectableGrid: React.FC<
  FilterBarSelectableGridProps
> = ({ filterBarProps, ...gridProps }) => {
  return (
    <FilterBarProvider {...filterBarProps}>
      <FilterBar />
      <FilteredSelectableGrid {...gridProps} />
    </FilterBarProvider>
  );
};

export const FilteredSelectableGrid: React.FC<SelectableAsyncGridProps> = ({
  children,
  ...gridProps
}) => {
  const [debouncedFilterValues] = useDebouncedFilterValues(500);

  return (
    <SelectableAsyncGrid
      search={debouncedFilterValues}
      refreshOnSearchChange
      {...gridProps}
    >
      {children}
    </SelectableAsyncGrid>
  );
};

export const FilterBarMainGrid: React.FC<FilterBarGridProps> = ({
  filterBarProps,
  disableUrlSync,
  ...gridProps
}) => {
  const { filterValues, filterConfigurations } = filterBarProps;
  const context = useGlobalContext();
  return filterBarProps.isColumnSelectorEnabled ? (
    <FilterBarProvider
      filterValues={filterValues}
      filterConfigurations={filterConfigurations}
      isOpen={filterBarProps.isOpen}
    >
      {!(
        context[0]?.currentUser?.isDoctor || context[0]?.currentUser?.isReviewer
      ) && <ColumnSelector />}
      <FilterBar isOpen={filterBarProps.isOpen} />
      <FilteredMainGrid
        hideFilterIcon={gridProps.hideFilterIcon}
        hideExportIcon={gridProps.hideExportIcon}
        disableUrlSync={disableUrlSync}
        {...gridProps}
      />
    </FilterBarProvider>
  ) : (
    <FilterBarProvider
      filterValues={filterValues}
      filterConfigurations={filterConfigurations}
    >
      <FilteredMainGrid
        hideFilterIcon={gridProps.hideFilterIcon}
        hideExportIcon={gridProps.hideExportIcon}
        disableUrlSync={disableUrlSync}
        {...gridProps}
      />
    </FilterBarProvider>
  );
};

export const FilteredMainGrid: React.FC<FilteredGridProps> = ({
  children,
  isMainGrid,
  hideFilterIcon,
  hideExportIcon,
  disableUrlSync,
  ...gridProps
}) => {
  return (
    <FilteredGrid
      isMainGrid
      hideFilterIcon={hideFilterIcon}
      hideExportIcon={hideExportIcon}
      disableUrlSync={disableUrlSync}
      {...gridProps}
    >
      {children}
    </FilteredGrid>
  );
};
